import i18nFactory from '@yandex-int/i18n';

import { parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { ValidationErrorData } from '../../typings/common';
import { ConstMetrikaKeyGoalsData } from '../../typings/constants';
import { Goal } from '../../typings/goals';
import {
    MetrikaKeyGoalDropdownItem,
    MetrikaKeyGoalItem,
    MetrikaKeyGoalsDropdownParams,
    MetrikaKeyGoalsEditorValidationResult,
    MetrikaKeyGoalSpecialId,
    MetrikaSelectedKeyGoal
} from '../../typings/metrikaKeyGoals';

import * as keyset from './MetrikaKeyGoalsEditorHelpers.i18n';

const i18n = i18nFactory(keyset);

export const getIsInvolvedSessionsOnly = (
    selectedGoals: MetrikaSelectedKeyGoal[],
    involvedSessionsId: string
): boolean => {
    return (
        selectedGoals.length === 1 && selectedGoals[0].id === involvedSessionsId
    );
};

export const validateKeyGoalsEditor = (
    selectedGoals: MetrikaSelectedKeyGoal[],
    constants: ConstMetrikaKeyGoalsData,
    currencySymbol: string,
    availableForKeyGoals: Record<string, Goal>,
    involvedSessionsId: string
): MetrikaKeyGoalsEditorValidationResult => {
    const isInvolvedSessionsOnly = getIsInvolvedSessionsOnly(
        selectedGoals,
        involvedSessionsId
    );

    const goalsValidationData = selectedGoals.map(selectedGoal => {
        return {
            goal: validateGoal(
                availableForKeyGoals,
                selectedGoal,
                involvedSessionsId
            ),
            conversionValue: validateMetrikaKeyGoalConversion(
                constants,
                currencySymbol,
                isInvolvedSessionsOnly,
                selectedGoal.conversionValue
            )
        };
    });

    const hasErrors = goalsValidationData.some(itemValidationData => {
        return (
            itemValidationData.conversionValue.length !== 0 ||
            itemValidationData.goal.length !== 0
        );
    });

    return {
        isValid: !hasErrors,
        keyGoalsValidationData: goalsValidationData
    };
};

const validateGoal = (
    availableForKeyGoals: Record<string, Goal>,
    goal: MetrikaSelectedKeyGoal,
    involvedSessionsId: string
): ValidationErrorData[] => {
    const errors = [];
    const goalItem = getGoalItem(
        availableForKeyGoals,
        goal,
        involvedSessionsId
    );

    switch (goalItem.id) {
        case MetrikaKeyGoalSpecialId.unavailable:
            errors.push({
                code: 'UNAVAILABLE_GOAL',
                text: i18n('unavailable-goal')
            });
            break;
        case MetrikaKeyGoalSpecialId.chooseGoal:
            errors.push({
                code: 'GOAL_NOT_CHOSEN',
                text: i18n('not-chosen-goal')
            });
            break;
        default:
            break;
    }

    return errors;
};

export const validateMetrikaKeyGoalConversion = (
    constants: ConstMetrikaKeyGoalsData,
    currencySymbol: string,
    isInvolvedSessionsOnly: boolean,
    conversionValue: string
): ValidationErrorData[] => {
    const errors = [];
    const { minConversion, maxConversion } = constants;

    if (isInvolvedSessionsOnly && conversionValue === '') {
        return [];
    }

    const conversionNumber = parseNumber(conversionValue);

    if (conversionNumber < minConversion) {
        errors.push({
            code: 'LESS_THEN_MIN_LIMIT',
            text: i18n('less-then-min-conversion', {
                minConversion,
                currencySymbol
            })
        });
    } else if (conversionNumber > maxConversion) {
        errors.push({
            code: 'MORE_THEN_MAX_LIMIT',
            text: i18n('more-then-max-conversion')
        });
    }

    return errors;
};

export const getGoalsDropdownItems = (
    availableForKeyGoals: Record<string, Goal>,
    selectedGoals: MetrikaSelectedKeyGoal[],
    involvedSessionsId: string
): MetrikaKeyGoalDropdownItem[] => {
    const selectedGoalsMap = selectedGoals.reduce(
        (acc: Record<string, MetrikaSelectedKeyGoal>, goal) => {
            acc[goal.id] = goal;

            return acc;
        },
        {}
    );

    const goalsKeys = Object.keys(availableForKeyGoals);

    const items = goalsKeys.map(goalKey => {
        return {
            ...availableForKeyGoals[goalKey],
            isDisabled: selectedGoalsMap[goalKey] !== undefined
        };
    });

    items.unshift({
        id: involvedSessionsId,
        name: i18n('involved-sessions'),
        isDisabled: selectedGoalsMap[involvedSessionsId] !== undefined
    });

    return items;
};

export const getMetrikaKeyGoalsDropdownParams = (
    selectedGoals: MetrikaSelectedKeyGoal[],
    availableForKeyGoals: Record<string, Goal>,
    isLoading: boolean,
    involvedSessionsId: string
): MetrikaKeyGoalsDropdownParams => {
    if (isLoading || selectedGoals.length === 0) {
        return {
            goalsDropdownItems: [],
            selectedGoalsItems: []
        };
    }

    return {
        goalsDropdownItems: getGoalsDropdownItems(
            availableForKeyGoals,
            selectedGoals,
            involvedSessionsId
        ),
        selectedGoalsItems: getSelectedGoalsItems(
            selectedGoals,
            availableForKeyGoals,
            isLoading,
            involvedSessionsId
        )
    };
};

export const getSelectedGoalsItems = (
    selectedGoals: MetrikaSelectedKeyGoal[],
    availableForKeyGoals: Record<string, Goal>,
    isLoading: boolean,
    involvedSessionsId: string
): MetrikaKeyGoalItem[] => {
    if (isLoading) {
        return [];
    }

    return selectedGoals.reduce((acc: MetrikaKeyGoalItem[], val) => {
        acc.push(getGoalItem(availableForKeyGoals, val, involvedSessionsId));

        return acc;
    }, []);
};

const getGoalItem = (
    availableForKeyGoals: Record<string, Goal>,
    goal: MetrikaSelectedKeyGoal,
    involvedSessionsId: string
): MetrikaKeyGoalItem => {
    switch (goal.id) {
        case involvedSessionsId:
            return {
                ...goal,
                name: i18n('involved-sessions')
            };
        case MetrikaKeyGoalSpecialId.chooseGoal:
            return {
                ...goal,
                name: i18n('choose-goal')
            };
        default: {
            const goalItem = availableForKeyGoals[goal.id];
            if (goalItem === undefined) {
                return {
                    ...goal,
                    id: MetrikaKeyGoalSpecialId.unavailable,
                    name: i18n('unavailable-goal')
                };
            }

            return {
                ...goal,
                name: goalItem.name
            };
        }
    }
};

export const getEditorErrorsLength = (
    savedGoalsItems: MetrikaKeyGoalItem[]
): number => {
    return savedGoalsItems.reduce((res: number, item: MetrikaKeyGoalItem) => {
        const itemErrors = item.errors;

        res += itemErrors.goal.length + itemErrors.conversionValue.length;

        return res;
    }, 0);
};
